/**
 * KCIS Identity Door — Identity Alignment Assessment Landing.
 * Visual design inspired by identity.christyvonta.com (Dr. Christy Vonta
 * Coaching & Consulting). Luxury palette: white · black · royal purple · gold accent.
 *
 * Preserves all backend assessment logic — only the visual + copy layer is new.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Heart, Brain, Compass, ArrowRight } from "lucide-react";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PALETTE = {
  purple: "#4C1D95",
  purpleSoft: "#7C3AED",
  gold: "#B8924B",
  ink: "#0B0B0F",
  paper: "#FAF8F4",
};

// Brand assets — provided by Dr. Christy Vonta (Feb 2026)
const LOGO_CV = "https://customer-assets.emergentagent.com/job_kissed-lite/artifacts/v3yr3vyl_C.jpg";
// Hero: black blazer in apothecary office — matches royal-purple/gold palette
const HERO_PORTRAIT = "https://customer-assets.emergentagent.com/job_kissed-lite/artifacts/79s8cv89_Dr.%20Christy%20Vonta%202.png";
// Bio: light-blue suit in elegant studio — softer, approachable
const BIO_PORTRAIT = "https://customer-assets.emergentagent.com/job_kissed-lite/artifacts/q1qr4hak_Dr.%20Christy%20Vonta%206.png";
// Clinical CTA: lab coat + purple scrubs — credentialed authority closer
const CLINICAL_PORTRAIT = "https://customer-assets.emergentagent.com/job_kissed-lite/artifacts/drj8gzxf_Dr.%20Christy%20Vonta%20full%20body%203.png";

export default function IdentityLanding() {
  useDocumentMeta({
    title: "Dr. Christy Vonta · Identity Alignment Assessment",
    description: "Free 40-question Identity Alignment Assessment from Dr. Christy Vonta — see where you truly are in your healing journey and receive a personalized reflection.",
  });
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="identity-landing">
      {/* HEADER */}
      <header className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/coaching/identity" className="flex items-center gap-3" data-testid="identity-brand">
            <img src={LOGO_CV} alt="Dr. Christy Vonta — Coaching & Consulting" className="w-12 h-12 object-contain rounded-full bg-white border border-slate-200" />
            <div className="leading-tight">
              <div className="text-base font-serif text-slate-900">Dr. <i style={{ color: PALETTE.purple }}>Christy Vonta</i></div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500">Coaching &amp; Consulting</div>
            </div>
          </Link>
          <nav className="flex items-center gap-7 text-sm">
            <Link to="/coaching/identity/assessment" className="text-slate-700 hover:text-purple-900" data-testid="nav-begin">Begin Assessment</Link>
            <a href="mailto:c.von@kissedbycvon.com" className="text-slate-700 hover:text-purple-900">Contact</a>
            <Link to="/admin/login" className="text-[11px] tracking-widest uppercase text-slate-500">Admin</Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="text-[11px] tracking-[0.22em] uppercase mb-6" style={{ color: PALETTE.purple }}>
            The Identity Alignment Assessment ——
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
            Who Is She…<br />
            <span className="italic" style={{ color: PALETTE.purple }}>Really?</span>
          </h1>
          <p className="mt-8 text-[15px] text-slate-700 leading-relaxed">
            An invitation from <i>Dr. Christy Vonta</i> for women ready to{" "}
            <a href="#why" className="underline decoration-[0.5px]" style={{ color: PALETTE.purple }}>heal</a>,{" "}
            <a href="#why" className="underline decoration-[0.5px]" style={{ color: PALETTE.purple }}>realign</a>, and{" "}
            <a href="#why" className="underline decoration-[0.5px]" style={{ color: PALETTE.purple }}>become</a>.
          </p>
          <hr className="my-8 w-24 border-slate-300" />
          <p className="text-[15px] text-slate-700 leading-relaxed max-w-xl">
            This is not just a quiz. This is an identity mirror. Many women spend years becoming who life, survival, relationships, family, career, motherhood, trauma, or society required them to be. This assessment helps you reflect on whether you are still living from those expectations — or walking boldly into who She Is meant to become.
          </p>
          <div className="mt-10 flex items-center gap-5">
            <Link
              to="/coaching/identity/assessment"
              className="inline-flex items-center gap-2 text-white text-sm tracking-wide rounded-full px-7 py-4 hover:opacity-90 transition"
              style={{ backgroundColor: PALETTE.purple }}
              data-testid="identity-begin-cta"
            >
              BEGIN MY ASSESSMENT <ArrowRight className="w-4 h-4" />
            </Link>
            <span className="text-xs tracking-wide text-slate-500" style={{ color: PALETTE.gold }}>
              ✦ 20 reflective prompts · ~5 minutes
            </span>
          </div>
        </div>
        <div className="lg:col-span-5 flex justify-center">
          <div className="relative">
            <div className="absolute -top-3 -left-3 w-24 h-24 rounded-full border border-amber-300/50" style={{ borderColor: PALETTE.gold }} />
            <div className="rounded-[28px] overflow-hidden shadow-xl w-[300px] h-[440px]" style={{ background: PALETTE.purple }}>
              <img src={HERO_PORTRAIT} alt="Dr. Christy Vonta" className="w-full h-[78%] object-cover object-top" data-testid="identity-hero-portrait" />
              <div className="px-5 py-4 text-white">
                <div className="font-serif italic text-sm leading-snug">"Healing begins with who you BELLieve you are."</div>
                <div className="text-[10px] tracking-widest mt-2 uppercase opacity-80" style={{ color: PALETTE.gold }}>— Dr. Christy Vonta · Coaching &amp; Consulting</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MEET DR CHRISTY */}
      <section className="bg-slate-50/60 border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 flex justify-center">
            <div className="rounded-[28px] overflow-hidden w-[280px] h-[400px] bg-slate-200">
              <img src={BIO_PORTRAIT} alt="Meet Dr. Christy Vonta" className="w-full h-full object-cover object-top" data-testid="identity-bio-portrait" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="text-[11px] tracking-[0.22em] uppercase mb-5" style={{ color: PALETTE.purple }}>The Guide Behind The Mirror</div>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight">Meet <span className="italic" style={{ color: PALETTE.purple }}>Dr. Christy Vonta</span></h2>
            <hr className="my-5 w-24" style={{ borderColor: PALETTE.gold }} />
            <div className="text-[11px] tracking-[0.22em] uppercase mb-5" style={{ color: PALETTE.gold }}>Identity-Based Holistic Wellness Coach</div>
            <p className="text-[15px] text-slate-700 leading-relaxed">
              Dr. Christy Vonta is an Identity-Based Holistic Wellness Coach, PhD in Psychology, Trauma-Informed Coach, Holistic Wellness Strategist, and founder of Christy Vonta Coaching &amp; Consulting.
            </p>
            <p className="mt-5 text-[15px] text-slate-700 leading-relaxed">
              She helps women reconnect with who they truly are beneath survival mode, expectations, burnout, trauma, and performance. Her work blends identity healing, mindset transformation, holistic wellness, and emotional alignment to help women heal what they cannot feel.
            </p>
            <blockquote className="mt-8 border-l-2 pl-5 italic font-serif text-lg" style={{ borderColor: PALETTE.gold, color: PALETTE.purple }}>
              "Healing begins with who you BELLieve you are."
            </blockquote>
            <div className="mt-8 flex flex-wrap gap-3">
              {["PhD in Psychology", "Trauma-Informed Coach", "Holistic Wellness Strategist"].map((c) => (
                <span key={c} className="text-[11px] uppercase tracking-wider rounded-full bg-white border border-slate-200 px-4 py-2 text-slate-600">
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY WOMEN CHOOSE DR CHRISTY */}
      <section id="why" className="max-w-6xl mx-auto px-6 py-24 text-center">
        <div className="text-[11px] tracking-[0.22em] uppercase mb-5" style={{ color: PALETTE.purple }}>Trusted by Women in Transformation</div>
        <h2 className="font-serif text-4xl sm:text-5xl leading-tight">Why Women Choose <span className="italic" style={{ color: PALETTE.purple }}>Dr. Christy</span></h2>
        <hr className="my-8 w-24 mx-auto" style={{ borderColor: PALETTE.purple }} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {[
            { icon: Heart, title: "Identity-Centered Healing", body: "Not surface-level motivation. Deep transformational work rooted in identity, healing, and alignment." },
            { icon: Brain, title: "Mind + Body + Wellness Integration", body: "Psychology, holistic wellness, emotional healing, and behavioral transformation combined." },
            { icon: Compass, title: "Real-Life Transformation", body: "Helping women move from survival mode into clarity, confidence, purpose, and becoming." },
          ].map((c, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-8 text-left">
              <div className="w-10 h-10 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: i === 2 ? PALETTE.purple : "#F3EAFB", color: i === 2 ? "white" : PALETTE.purple }}>
                <c.icon className="w-5 h-5" />
              </div>
              <hr className="w-10 mb-5" style={{ borderColor: PALETTE.gold }} />
              <h3 className="font-serif text-xl text-slate-900 leading-snug">{c.title}</h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* IDENTITY MIRROR PURPLE BANNER */}
      <section className="text-white text-center py-24 px-6" style={{ backgroundColor: PALETTE.purple }}>
        <div className="text-3xl mb-6" style={{ color: PALETTE.gold }}>"</div>
        <h2 className="font-serif text-4xl sm:text-5xl leading-tight">
          This is not just a quiz.<br />
          This is an <span className="italic" style={{ color: PALETTE.gold }}>identity mirror</span>.
        </h2>
        <hr className="my-8 w-24 mx-auto" style={{ borderColor: PALETTE.gold }} />
      </section>

      {/* FOUR ARCHETYPES */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
          <div className="lg:col-span-6">
            <div className="text-[11px] tracking-[0.22em] uppercase mb-5" style={{ color: PALETTE.purple }}>Four Identity Archetypes</div>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight" style={{ color: PALETTE.purple }}>
              Discover which season of becoming <i className="text-slate-900">she</i> is walking through.
            </h2>
          </div>
          <div className="lg:col-span-6 flex items-end">
            <p className="text-[15px] text-slate-700 leading-relaxed">
              Your responses are scored across five thematic sections — Identity &amp; Roles, Emotional Suppression, Self-Worth, Healing Readiness, and Becoming — to reveal one of four archetypes and a personalized next step.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { roman: "I", title: "Survival Mode", body: "You have learned how to hold the weight of everyone." },
            { roman: "II", title: "Awakening", body: "You sense the woman you were quietly becoming." },
            { roman: "III", title: "Rebuilding", body: "You are choosing yourself, deliberately and gently." },
            { roman: "IV", title: "Becoming", body: "You are walking boldly into who She Is." },
          ].map((a, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="font-serif text-xl mb-4" style={{ color: PALETTE.gold }}>{a.roman}</div>
              <h3 className="font-serif text-lg" style={{ color: PALETTE.purple }}>{a.title}</h3>
              <hr className="my-3 w-10 border-slate-200" />
              <p className="text-sm text-slate-600 leading-relaxed">{a.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* THE BELL FRAMEWORK */}
      <section className="bg-slate-50/60 border-y border-slate-100 py-24 px-6" data-testid="identity-bell-framework">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-7">
              <div className="text-[11px] tracking-[0.22em] uppercase mb-5" style={{ color: PALETTE.purple }}>A Signature Methodology</div>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-slate-900">
                The <span className="italic" style={{ color: PALETTE.purple }}>BELL</span> Framework
                <sup className="text-sm align-super ml-1 text-slate-400">TM</sup>
              </h2>
            </div>
            <div className="lg:col-span-5 flex items-end">
              <p className="text-[15px] text-slate-600 leading-relaxed">
                A four-stage philosophy that guides every woman from inherited identity to embodied becoming — the core intellectual framework of Dr. Christy Vonta's coaching practice.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { letter: "B", num: "01", word: "Believe", body: "Challenge inherited identities and limiting beliefs." },
              { letter: "E", num: "02", word: "Embrace", body: "Acknowledge your truth, emotions, and healing journey." },
              { letter: "L", num: "03", word: "Lead",    body: "Lead yourself intentionally instead of surviving reactively." },
              { letter: "L", num: "04", word: "Live",    body: "Live aligned, empowered, and unapologetically whole." },
            ].map((b) => (
              <div key={b.num} className="rounded-2xl bg-white border border-slate-200 p-7 shadow-sm" data-testid={`bell-card-${b.num}`}>
                <div className="flex items-start justify-between">
                  <div className="font-serif text-6xl leading-none" style={{ color: "#E5DAF5" }}>{b.letter}</div>
                  <div className="text-[10px] tracking-widest mt-2" style={{ color: PALETTE.gold }}>{b.num}</div>
                </div>
                <hr className="my-5 w-10" style={{ borderColor: PALETTE.gold }} />
                <div className="font-serif text-lg" style={{ color: PALETTE.purple }}>
                  {b.letter} <span className="text-slate-400">—</span> <i>{b.word}</i>
                </div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="font-serif italic text-xl sm:text-2xl" style={{ color: PALETTE.purple }}>
              "Believe. Embrace. Lead. Live."
            </div>
            <div className="mt-3 text-[11px] tracking-[0.28em] uppercase" style={{ color: PALETTE.purple }}>
              The Four Movements of Becoming
            </div>
          </div>
        </div>
      </section>

      {/* READY WHEN YOU ARE — Final CTA (luxury card design) */}
      <section className="bg-white py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-[32px] bg-white shadow-[0_30px_60px_-30px_rgba(76,29,149,0.18)] border border-slate-100 p-10 sm:p-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-testid="identity-ready-card">
            <div className="lg:col-span-7">
              <div className="text-[11px] tracking-[0.22em] uppercase mb-6" style={{ color: PALETTE.purple }}>Ready When You Are</div>
              <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight" style={{ color: PALETTE.purple }}>
                Step into the mirror and meet the woman she is becoming.
              </h3>
              <p className="mt-8 text-[15px] italic font-serif text-slate-500 leading-relaxed max-w-xl">
                You do not have to become someone new. You simply have to reconnect with the woman you were before survival made you forget.
              </p>
            </div>
            <div className="lg:col-span-5 flex lg:justify-end">
              <Link
                to="/coaching/identity/assessment"
                className="inline-flex items-center gap-2 text-white text-sm tracking-wide rounded-full px-8 py-5 hover:opacity-90 transition shadow-lg"
                style={{ backgroundColor: PALETTE.purple, boxShadow: "0 14px 30px -10px rgba(76,29,149,0.45)" }}
                data-testid="identity-begin-cta-bottom"
              >
                BEGIN MY ASSESSMENT <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Clinical authority strip (preserves the lab-coat brand asset) */}
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-4 flex justify-center lg:justify-start">
              <div className="rounded-[24px] overflow-hidden w-[220px] h-[300px] bg-slate-100">
                <img src={CLINICAL_PORTRAIT} alt="Dr. Christy Vonta, clinical authority" className="w-full h-full object-cover object-top" data-testid="identity-clinical-portrait" />
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="text-[11px] tracking-[0.22em] uppercase mb-3" style={{ color: PALETTE.gold }}>Credentialed Authority</div>
              <p className="text-[15px] text-slate-700 leading-relaxed max-w-2xl">
                Dr. Christy Vonta brings clinical rigor, psychological depth, and lived embodiment to every step of the BELL Framework — guiding women from awareness into aligned action.
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-slate-50/60 border-t border-slate-200" data-testid="identity-footer">
        <div className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <img src={LOGO_CV} alt="Dr. Christy Vonta — Coaching & Consulting" className="w-14 h-14 object-contain rounded-full bg-white border border-slate-200" />
              <div className="leading-tight">
                <div className="text-base font-serif text-slate-900">Dr. <i style={{ color: PALETTE.purple }}>Christy Vonta</i></div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-slate-500">Coaching &amp; Consulting</div>
              </div>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-[11px] tracking-[0.22em] uppercase mb-4" style={{ color: PALETTE.purple }}>Explore</div>
            <ul className="space-y-3 text-sm text-slate-700">
              <li><Link to="/coaching/identity" className="hover:text-purple-900" data-testid="footer-link-home">Home</Link></li>
              <li><Link to="/coaching/identity/assessment" className="hover:text-purple-900" data-testid="footer-link-begin">Begin Assessment</Link></li>
              <li><a href="mailto:c.von@kissedbycvon.com" className="hover:text-purple-900" data-testid="footer-link-contact">Contact</a></li>
            </ul>
          </div>

          <div className="md:col-span-4 md:text-right">
            <p className="font-serif italic text-base" style={{ color: PALETTE.purple }}>
              Healing begins with who you BELLieve you are.
            </p>
            <p className="mt-3 text-[10px] tracking-[0.22em] uppercase text-slate-500">
              BELL is the philosophy. Christy Vonta is the guide.
            </p>
          </div>
        </div>

        <div className="border-t border-slate-200">
          <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[10px] tracking-[0.22em] uppercase text-slate-500">
            <div>© 2026 Dr. Christy Vonta Coaching &amp; Consulting</div>
            <div>The Identity Alignment Assessment</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
